import {
  CalendarOutlined,
  EnvironmentOutlined,
  FolderOutlined,
  InfoCircleOutlined,
  TeamOutlined
} from "@ant-design/icons"
import { Button, Flex, Select, Tag, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { neutral } from "../../../../components/widgets/tokens.ts"
import {
  filterByLabelOrValue,
  mergeSelectedIntoOptions
} from "../../../DashboardPage/components/Portfolio/FilterBar.tsx"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import {
  setReportCompare,
  setReportFilters,
  setReportWindow
} from "../../helpers/reportDefinition.ts"
import { FILTER_DIMENSION_LABEL_KEYS } from "../../helpers/reportFilterSummary.ts"
import { countActiveReportFilters } from "../../helpers/reportWidgetFilters.ts"
import { useReportFilterOptions } from "../../hooks/useReportFilterOptions.ts"
import { reportCanvasApplyDefinition } from "../../redux/reportCanvas.slice.ts"
import type {
  ReportCompareValue,
  ReportFilterDimension,
  ReportWindow
} from "../../types/canvas.types.ts"
import { ReportCompareControl } from "../canvas/ReportCompareControl.tsx"
import { ReportDateRangeControl } from "../canvas/ReportDateRangeControl.tsx"
import { ReportFiltersSummary } from "./ReportFiltersSummary.tsx"

const { Text, Title } = Typography

const FilterGroup = ({
  icon,
  title,
  children
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) => (
  <section
    aria-label={title}
    className="rounded-xl border border-solid bg-white p-4"
    style={{ borderColor: neutral.border }}
  >
    <Flex align="center" gap="small" className="mb-3">
      <span aria-hidden style={{ color: neutral.muted }}>
        {icon}
      </span>
      <Text strong>{title}</Text>
    </Flex>
    <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3">
      {children}
    </div>
  </section>
)

/**
 * The report's default filters, grouped so a single-field group is one compact
 * card rather than a full-width row. What they mean is unchanged: the same
 * definition fields the report has always saved.
 */
export const ReportFiltersStep = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const definition = useAppSelector((s) => s.reportCanvas.definition)
  const { options, loading } = useReportFilterOptions()

  const filters = definition?.filters ?? {}
  const activeCount = countActiveReportFilters(filters)

  const setWindow = (window: ReportWindow) => {
    if (!definition) return
    dispatch(reportCanvasApplyDefinition(setReportWindow(definition, window)))
  }
  const setCompare = (compare: ReportCompareValue) => {
    if (!definition) return
    dispatch(reportCanvasApplyDefinition(setReportCompare(definition, compare)))
  }
  const setDim = (dimension: ReportFilterDimension, value: string[]) => {
    if (!definition) return
    dispatch(
      reportCanvasApplyDefinition(
        setReportFilters(definition, { ...filters, [dimension]: value })
      )
    )
  }
  const clearDims = () => {
    if (!definition) return
    dispatch(reportCanvasApplyDefinition(setReportFilters(definition, {})))
  }

  const field = (label: string, control: React.ReactNode) => (
    <Flex vertical gap={4} className="min-w-0">
      <Text type="secondary" className="text-xs">
        {label}
      </Text>
      {control}
    </Flex>
  )

  const dimSelect = (dimension: ReportFilterDimension) =>
    field(
      t(FILTER_DIMENSION_LABEL_KEYS[dimension]),
      <Select<string[]>
        mode="multiple"
        allowClear
        showSearch
        loading={loading}
        maxTagCount="responsive"
        filterOption={filterByLabelOrValue}
        aria-label={t(FILTER_DIMENSION_LABEL_KEYS[dimension])}
        placeholder={t("reports.filtersStep.summary.all")}
        value={filters[dimension] ?? []}
        options={mergeSelectedIntoOptions(
          options[dimension],
          filters[dimension]
        )}
        onChange={(value) => setDim(dimension, value)}
        style={{ width: "100%" }}
      />
    )

  return (
    <div className="mx-auto grid w-full max-w-[1200px] grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
      <Flex vertical gap="middle" className="min-w-0">
        <Flex vertical gap={2}>
          <Flex align="center" gap="small">
            <Title level={3} className="m-0 text-start">
              {t("reports.filtersStep.title")}
            </Title>
            <Tag bordered={false} className="m-0">
              {t("reports.wizard.optionalStep")}
            </Tag>
          </Flex>
          <Text type="secondary">{t("reports.filtersStep.subtitle")}</Text>
        </Flex>

        <FilterGroup
          icon={<CalendarOutlined />}
          title={t("reports.filtersStep.cards.dateRange")}
        >
          {field(
            t("reports.filtersStep.fields.datePreset"),
            <ReportDateRangeControl
              window={definition?.window}
              onChange={setWindow}
            />
          )}
          {field(
            t("reports.filtersStep.fields.compareTo"),
            <ReportCompareControl
              compare={definition?.compare}
              onChange={setCompare}
            />
          )}
        </FilterGroup>

        <FilterGroup
          icon={<FolderOutlined />}
          title={t("reports.filtersStep.cards.projects")}
        >
          {dimSelect("project")}
          {dimSelect("project_status")}
          {dimSelect("project_type")}
        </FilterGroup>

        <FilterGroup
          icon={<TeamOutlined />}
          title={t("reports.filtersStep.cards.people")}
        >
          {dimSelect("client")}
          {dimSelect("pm")}
        </FilterGroup>

        <FilterGroup
          icon={<EnvironmentOutlined />}
          title={t("reports.filtersStep.cards.leadsAndLocation")}
        >
          {dimSelect("status")}
          {dimSelect("state")}
        </FilterGroup>
      </Flex>

      <aside
        aria-label={t("reports.filtersStep.summary.title")}
        className="flex flex-col gap-3 rounded-xl border border-solid bg-white p-4 lg:sticky lg:top-0"
        style={{ borderColor: neutral.border }}
      >
        <Flex align="center" justify="space-between">
          <Text strong>{t("reports.filtersStep.summary.title")}</Text>
          <Button
            size="small"
            type="link"
            className="px-0"
            disabled={activeCount === 0}
            onClick={clearDims}
          >
            {t("reports.filtersStep.clearAll")}
          </Button>
        </Flex>
        <ReportFiltersSummary definition={definition} />
        <Flex
          gap="small"
          className="rounded-lg p-2.5"
          style={{ background: "rgba(42,125,140,.06)" }}
        >
          <InfoCircleOutlined
            aria-hidden
            style={{ color: neutral.muted, marginTop: 2 }}
          />
          <Text type="secondary" className="text-xs">
            {t("reports.filtersStep.summary.note")}
          </Text>
        </Flex>
      </aside>
    </div>
  )
}
